import { showServiceStart, showServiceStop } from "@/components/environments/service-actions";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useStartService, useStopService, type PlainService } from "@/queries/automations-queries";
import { formatError } from "@/utils/errors";
import { useCallback } from "react";

type ServiceToggleAction = "start" | "stop" | "none";

export function serviceToggleAction(service: PlainService): ServiceToggleAction {
    if (showServiceStart(service)) return "start";
    if (showServiceStop(service)) return "stop";
    return "none";
}

export function useToggleService(environmentId: string) {
    const startService = useStartService();
    const stopService = useStopService();
    const { toast } = useToast();

    const toggle = useCallback(
        async (service: PlainService, shouldStart: boolean) => {
            const name = service.metadata?.name || "service";
            try {
                if (shouldStart) {
                    await startService.mutateAsync({ serviceId: service.id, environmentId });
                } else {
                    await stopService.mutateAsync({ serviceId: service.id, environmentId });
                }
            } catch (error) {
                toast({
                    title: `Failed to ${shouldStart ? "start" : "stop"} ${name}`,
                    description: formatError(error),
                });
            }
        },
        [startService, stopService, environmentId, toast],
    );

    return toggle;
}
